import React, {
  type CSSProperties,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Card, CardContent, Switch, Typography } from "@material-ui/core";
import { connect } from "react-redux";
import type { AnyAction } from "redux";

import { Trans, useTranslations } from "adapters/i18n";
import ConfirmationModal from "features/shared/components/confirmationModal";
import { updateFarmBenchmarkParticipation } from "features/manejo/redux/actions/farmActions";
import type { RootState } from "redux/reducers";
import theme from "styles/theme";
import { ThunkDispatch } from "redux-thunk";

type FarmBenchmarkCardProps = {
  fazenda: RootState["fazenda"];
  onUpdateBenchmarkParticipation: (participacao: boolean) => Promise<boolean>;
};

export function FarmBenchmarkCard({
  fazenda,
  onUpdateBenchmarkParticipation,
}: FarmBenchmarkCardProps) {
  const { t } = useTranslations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [farmIsParticipatingInBenchmark, setFarmIsParticipatingInBenchmark] =
    useState(fazenda.currentFarm?.ParticipacaoBenchmark ?? false);

  const [isUpdatingParticipation, setIsUpdatingParticipation] = useState(false);

  const benchmarkToggle = useMemo(
    () =>
      fazenda.Toggles.find(
        (toggle) => toggle.Nome === "CONFIGURAR_PARTICIPACAO_BENCHMARK",
      ),
    [fazenda.Toggles],
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleToggleChange = useCallback(async () => {
    if (farmIsParticipatingInBenchmark) {
      setIsModalOpen(true);
      return;
    }

    await onUpdateBenchmarkParticipation(true);
    setFarmIsParticipatingInBenchmark(true);
  }, [farmIsParticipatingInBenchmark, onUpdateBenchmarkParticipation]);

  const handleSubmit = useCallback(async () => {
    setIsUpdatingParticipation(true);

    if (isUpdatingParticipation) {
      return;
    }

    await onUpdateBenchmarkParticipation(false);
    setFarmIsParticipatingInBenchmark(false);
    setIsUpdatingParticipation(false);

    closeModal();
  }, [onUpdateBenchmarkParticipation, closeModal, isUpdatingParticipation]);

  if (!benchmarkToggle) {
    return null;
  }

  return (
    <div style={styles.cardWrapper}>
      <Card
        component="article"
        aria-labelledby="benchmark-title"
        style={styles.card}
      >
        <CardContent style={styles.cardContent}>
          <figure style={styles.iconWrapper} aria-hidden="true">
            <img src="/images/ico-monitoring.svg" alt="" style={styles.icon} />
          </figure>

          <div style={styles.mainContent}>
            <header>
              <Typography
                id="benchmark-title"
                component="h2"
                variant="h6"
                style={styles.title}
              >
                {t("locales:farm-benchmark")}
              </Typography>

              <Typography component="p" style={styles.description}>
                {t("benchmark:compare-the-performance-of-your-farm")}
              </Typography>
            </header>

            <section style={styles.content}>
              <Typography component="h3" style={styles.sectionTitle}>
                {t("benchmark:benchmark-participation")}
              </Typography>

              <div style={styles.switchContainer}>
                <Typography component="p" style={styles.switchLabel}>
                  {t("benchmark:do-you-want-to-participate-in-the-benchmark")}
                </Typography>

                <div style={styles.switchWrapper}>
                  <span style={styles.switchOff}>{t("common:actions.no")}</span>

                  <Switch
                    color="secondary"
                    checked={farmIsParticipatingInBenchmark}
                    onChange={handleToggleChange}
                  />

                  <span style={styles.switchOn}>{t("common:actions.yes")}</span>
                </div>
              </div>
            </section>
          </div>

          <div style={styles.badgeWrapper}>
            <span style={styles.badge} aria-label={t("common:new")}>
              {t("common:new")}!
            </span>
          </div>
        </CardContent>
      </Card>

      {isModalOpen && (
        <ConfirmationModal
          title={t("benchmark:benchmark-participation")}
          text={<Trans i18nKey="benchmark:cancel-participation-text" />}
          cancelButtonTitle={t("common:actions.no")}
          submitButtonTitle={t("common:actions.yes")}
          onClose={closeModal}
          onCancel={closeModal}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  cardWrapper: {
    gridColumn: "1 / -1",
    width: "100%",
    padding: "0 14px",
    marginTop: 32,
    justifyContent: "center",
  },
  card: {
    backgroundColor: "#FAFAFA",
  },
  cardContent: {
    padding: 13,
    display: "grid",
    gridTemplateColumns: "40px 1fr auto",
    gap: 16,
    alignItems: "flex-start",
  },
  mainContent: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    margin: 0,
  },
  icon: {
    width: 20,
    height: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  description: {
    fontSize: 14,
    fontWeight: 300,
    lineHeight: 1.43,
    color: "#2A2A2A",
  },
  content: {
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 8,
    color: "#2A2A2A",
  },
  switchContainer: {
    backgroundColor: "#ECF0F3",
    borderRadius: 8,
    padding: "2px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    maxWidth: 632,
  },
  switchLabel: {
    fontSize: 14,
    color: "#2A2A2A",
  },
  switchWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    fontSize: 14,
    fontFamily: "Roboto",
  },
  switchOff: {
    color: "#9E9E9E",
  },
  switchOn: {
    color: "#2A2A2A",
  },
  badgeWrapper: {
    alignSelf: "flex-start",
  },
  badge: {
    backgroundColor: "#CCE6FF",
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 500,
    padding: "5px 20px",
    borderRadius: 4,
    whiteSpace: "nowrap",
    fontFamily: "Roboto",
  },
};

const mapStateToProps = (state: RootState) => ({
  fazenda: state.fazenda,
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
) => ({
  onUpdateBenchmarkParticipation: (participacao: boolean) =>
    dispatch(updateFarmBenchmarkParticipation(participacao)),
});

export const FarmBenchmarkCardContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(FarmBenchmarkCard);
